import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Box, HStack, Button, Text, VStack } from '@chakra-ui/react';
import '../styles/commentContent.css';

export interface MentionUser {
  id: string;
  name: string;
  email?: string;
}

interface CommentRichEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: string | number;
  disabled?: boolean;
  /** On mobile: contentEditable only after user taps the field */
  requireTapToEdit?: boolean;
  /** Users available for @mentions */
  mentionUsers?: MentionUser[];
}

const exec = (command: string, value?: string) => {
  document.execCommand(command, false, value);
};

/* Inline SVG icons (no extra dependency) */
const IconBold = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" /><path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" /></svg>
);
const IconItalic = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="4" x2="10" y2="4" /><line x1="14" y1="20" x2="5" y2="20" /><line x1="15" y1="4" x2="9" y2="20" /></svg>
);
const IconUnderline = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 3v7a6 6 0 0 0 12 0V3" /><line x1="4" y1="21" x2="20" y2="21" /></svg>
);
const IconListBullet = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>
);
const IconListOrdered = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="10" y1="6" x2="21" y2="6" /><line x1="10" y1="12" x2="21" y2="12" /><line x1="10" y1="18" x2="21" y2="18" /><path d="M4 6h1v4" /><path d="M4 10h2" /><path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" /></svg>
);
const IconAlignLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="21" y1="6" x2="3" y2="6" /><line x1="15" y1="12" x2="3" y2="12" /><line x1="17" y1="18" x2="3" y2="18" /></svg>
);
const IconAlignCenter = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="21" y1="6" x2="3" y2="6" /><line x1="17" y1="12" x2="7" y2="12" /><line x1="19" y1="18" x2="5" y2="18" /></svg>
);
const IconAlignRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="21" y1="6" x2="3" y2="6" /><line x1="21" y1="12" x2="9" y2="12" /><line x1="21" y1="18" x2="7" y2="18" /></svg>
);

function getMentionQueryAtCaret(editor: HTMLElement): { query: string; range: Range } | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return null;
  const range = sel.getRangeAt(0);
  if (!editor.contains(range.startContainer)) return null;

  const probe = range.cloneRange();
  probe.selectNodeContents(editor);
  probe.setEnd(range.startContainer, range.startOffset);
  const textBefore = probe.toString();
  const match = textBefore.match(/(^|[\s\u00A0])@([^\s@]*)$/);
  if (!match) return null;

  const query = match[2] || '';
  const atIndexInBefore = textBefore.length - query.length - 1;

  // Build a range covering "@query"
  const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
  let seen = 0;
  let startNode: Text | null = null;
  let startOffset = 0;
  let endNode: Text | null = null;
  let endOffset = 0;
  let node: Node | null;
  while ((node = walker.nextNode())) {
    const text = node.textContent || '';
    const next = seen + text.length;
    if (!startNode && atIndexInBefore >= seen && atIndexInBefore < next) {
      startNode = node as Text;
      startOffset = atIndexInBefore - seen;
    }
    if (!endNode && textBefore.length >= seen && textBefore.length <= next) {
      endNode = node as Text;
      endOffset = textBefore.length - seen;
      break;
    }
    seen = next;
  }
  if (!startNode || !endNode) return null;

  const mentionRange = document.createRange();
  mentionRange.setStart(startNode, startOffset);
  mentionRange.setEnd(endNode, endOffset);
  return { query, range: mentionRange };
}

export const CommentRichEditor: React.FC<CommentRichEditorProps> = ({
  value,
  onChange,
  placeholder = 'Write a comment...',
  minHeight = '80px',
  disabled = false,
  requireTapToEdit = false,
  mentionUsers = [],
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [editing, setEditing] = useState(!requireTapToEdit);
  const skipNextExternalSync = useRef(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);
  const mentionRangeRef = useRef<Range | null>(null);

  useEffect(() => {
    setEditing(!requireTapToEdit);
  }, [requireTapToEdit]);

  // Load initial / external value into contentEditable (mount + when parent replaces text)
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (skipNextExternalSync.current) {
      skipNextExternalSync.current = false;
      return;
    }
    const next = value || '';
    if (el.innerHTML !== next) {
      el.innerHTML = next;
    }
  }, [value]);

  const emitChange = useCallback(() => {
    skipNextExternalSync.current = true;
    const html = ref.current?.innerHTML ?? '';
    onChange(html);
  }, [onChange]);

  const filteredMentions = mentionUsers
    .filter((u) => {
      const q = mentionQuery.toLowerCase();
      if (!q) return true;
      return (
        u.name.toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
      );
    })
    .slice(0, 8);

  const updateMentionState = useCallback(() => {
    const el = ref.current;
    if (!el || !mentionUsers.length) {
      setMentionOpen(false);
      return;
    }
    const found = getMentionQueryAtCaret(el);
    if (!found) {
      setMentionOpen(false);
      mentionRangeRef.current = null;
      return;
    }
    mentionRangeRef.current = found.range;
    setMentionQuery(found.query);
    setMentionIndex(0);
    setMentionOpen(true);
  }, [mentionUsers.length]);

  const insertMention = useCallback(
    (user: MentionUser) => {
      const el = ref.current;
      const mentionRange = mentionRangeRef.current;
      if (!el || !mentionRange) return;

      const sel = window.getSelection();
      if (!sel) return;

      sel.removeAllRanges();
      sel.addRange(mentionRange);
      mentionRange.deleteContents();

      const span = document.createElement('span');
      span.className = 'comment-mention';
      span.setAttribute('data-user-id', user.id);
      span.setAttribute('data-user-name', user.name);
      span.contentEditable = 'false';
      span.textContent = `@${user.name}`;

      mentionRange.insertNode(span);

      const space = document.createTextNode('\u00A0');
      span.after(space);

      const after = document.createRange();
      after.setStartAfter(space);
      after.collapse(true);
      sel.removeAllRanges();
      sel.addRange(after);

      setMentionOpen(false);
      mentionRangeRef.current = null;
      el.focus();
      emitChange();
    },
    [emitChange],
  );

  const onInput = useCallback(() => {
    emitChange();
    updateMentionState();
  }, [emitChange, updateMentionState]);

  const onPaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  }, []);

  const wrapSelectionInTag = useCallback((tagName: string) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (range.collapsed) {
      const el = document.createElement(tagName);
      el.appendChild(document.createTextNode('\u00A0'));
      range.insertNode(el);
      range.setStart(el, 0);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      const el = document.createElement(tagName);
      try {
        range.surroundContents(el);
      } catch {
        const fragment = range.extractContents();
        el.appendChild(fragment);
        range.insertNode(el);
      }
      range.selectNodeContents(el);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, []);

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (mentionOpen && filteredMentions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex((i) => (i + 1) % filteredMentions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex((i) => (i - 1 + filteredMentions.length) % filteredMentions.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(filteredMentions[mentionIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionOpen(false);
        return;
      }
    }

    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          exec('bold');
          return;
        case 'i':
          e.preventDefault();
          wrapSelectionInTag('em');
          emitChange();
          return;
        case 'u':
          e.preventDefault();
          exec('underline');
          return;
        case '7':
          if (e.shiftKey) {
            e.preventDefault();
            exec('insertOrderedList');
            return;
          }
          break;
        case '8':
          if (e.shiftKey) {
            e.preventDefault();
            exec('insertUnorderedList');
            return;
          }
          break;
        default:
          break;
      }
    }
  }, [
    mentionOpen,
    filteredMentions,
    mentionIndex,
    insertMention,
    wrapSelectionInTag,
    emitChange,
  ]);

  const trigger = (command: string, value?: string) => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    if (command === 'italic') {
      wrapSelectionInTag('em');
    } else if (command === 'insertUnorderedList' || command === 'insertOrderedList') {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        if (range.collapsed) {
          const block = document.createElement(command === 'insertUnorderedList' ? 'ul' : 'ol');
          const li = document.createElement('li');
          li.appendChild(document.createTextNode('\u00A0'));
          block.appendChild(li);
          range.insertNode(block);
          range.setStart(li, 0);
          range.setEnd(li, 0);
          sel.removeAllRanges();
          sel.addRange(range);
        } else {
          exec(command, value);
        }
      } else {
        exec(command, value);
      }
    } else {
      exec(command, value);
    }
    emitChange();
  };

  const keepFocus = (e: React.MouseEvent) => e.preventDefault();

  const activateEditor = () => {
    if (!editing && requireTapToEdit && !disabled) {
      setEditing(true);
      requestAnimationFrame(() => ref.current?.focus());
    }
  };

  return (
    <Box width="100%" position="relative" ref={wrapRef}>
      {editing && (
      <HStack gap={1} mb={2} flexWrap="wrap">
        <Button size="xs" variant="outline" onMouseDown={keepFocus} onClick={() => trigger('bold')} title="Podebljano (Ctrl+B)" aria-label="Podebljano">
          <IconBold />
        </Button>
        <Button size="xs" variant="outline" onMouseDown={keepFocus} onClick={() => trigger('italic')} title="Kurziv (Ctrl+I)" aria-label="Kurziv">
          <IconItalic />
        </Button>
        <Button size="xs" variant="outline" onMouseDown={keepFocus} onClick={() => trigger('underline')} title="Podvučeno (Ctrl+U)" aria-label="Podvučeno">
          <IconUnderline />
        </Button>
        <Box w="1px" h="5" bg="gray.300" mx={1} />
        <Button size="xs" variant="outline" onMouseDown={keepFocus} onClick={() => trigger('insertUnorderedList')} title="Lista sa tačkama (Ctrl+Shift+8)" aria-label="Lista">
          <IconListBullet />
        </Button>
        <Button size="xs" variant="outline" onMouseDown={keepFocus} onClick={() => trigger('insertOrderedList')} title="Numerisana lista (Ctrl+Shift+7)" aria-label="Numerisana lista">
          <IconListOrdered />
        </Button>
        <Box w="1px" h="5" bg="gray.300" mx={1} />
        <Button size="xs" variant="outline" onMouseDown={keepFocus} onClick={() => trigger('justifyLeft')} title="Poravnaj levo" aria-label="Levo">
          <IconAlignLeft />
        </Button>
        <Button size="xs" variant="outline" onMouseDown={keepFocus} onClick={() => trigger('justifyCenter')} title="Centrirano" aria-label="Centar">
          <IconAlignCenter />
        </Button>
        <Button size="xs" variant="outline" onMouseDown={keepFocus} onClick={() => trigger('justifyRight')} title="Poravnaj desno" aria-label="Desno">
          <IconAlignRight />
        </Button>
        {mentionUsers.length > 0 && (
          <Text fontSize="xs" color="gray.500" ml={1}>
            Type @ to mention
          </Text>
        )}
      </HStack>
      )}
      <div
        ref={ref}
        className="comment-html-content comment-rich-editor-content"
        contentEditable={!disabled && editing}
        suppressContentEditableWarning
        onInput={onInput}
        onPaste={onPaste}
        onKeyDown={onKeyDown}
        onClick={activateEditor}
        onKeyUp={updateMentionState}
        data-placeholder={placeholder}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          minHeight: typeof minHeight === 'number' ? `${minHeight}px` : minHeight,
          padding: '8px 12px',
          border: '1px solid var(--chakra-colors-gray-200)',
          borderRadius: '6px',
          backgroundColor: editing ? 'white' : 'var(--chakra-colors-gray-50)',
          fontSize: '14px',
          outline: 'none',
          cursor: requireTapToEdit && !editing ? 'text' : 'text',
        }}
      />
      {mentionOpen && filteredMentions.length > 0 && (
        <Box
          position="absolute"
          left={0}
          right={0}
          bottom="100%"
          mb={1}
          zIndex={20}
          bg="white"
          borderWidth={1}
          borderColor="gray.200"
          borderRadius="lg"
          boxShadow="lg"
          maxH="220px"
          overflowY="auto"
          py={1}
        >
          <VStack align="stretch" gap={0}>
            {filteredMentions.map((user, idx) => (
              <button
                key={user.id}
                type="button"
                style={{
                  textAlign: 'left',
                  padding: '8px 12px',
                  background: idx === mentionIndex ? '#eff6ff' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  width: '100%',
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  insertMention(user);
                }}
                onMouseEnter={() => setMentionIndex(idx)}
              >
                <Text fontSize="sm" fontWeight="medium" color="gray.800">
                  {user.name}
                </Text>
                {user.email && (
                  <Text fontSize="xs" color="gray.500">
                    {user.email}
                  </Text>
                )}
              </button>
            ))}
          </VStack>
        </Box>
      )}
    </Box>
  );
};

export default CommentRichEditor;
